import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@rebass/emotion';

import { ShopStoreProvider } from '@app/shop/context/ShopStoreContext';
import { CartProvider } from '@app/shop/context/CartContext';
import useCart from '@app/shop/hooks/useCart';
import Alerts from '@app/shop/components/Alerts';
import Link from '@app/components/Link';
import PageTitle from '@app/components/PageTitle';
import ErrorBoundary from '@app/components/ErrorBoundary';

import AuthCheck from '../../components/AuthCheck';
import CartStatusCheck from '../../components/CartStatusCheck';
import {
  Skeleton,
  SubHeader,
  SubHeaderTitle,
} from '../../components/Skeleton/Skeleton';
import StepsProgress from '../../components/StepsProgress/StepsProgress';
import messages from './CheckoutLayout.messages';

const CheckoutLayout: FC = ({ children }) => (
  <AuthCheck>
    <Skeleton>
      <PageTitle>{messages.title}</PageTitle>

      <SubHeader>
        <SubHeaderTitle>
          <Link to="/:language/checkout/">
            <FormattedMessage {...messages.title} />
          </Link>
        </SubHeaderTitle>
      </SubHeader>

      <ErrorBoundary>
        <ShopStoreProvider>
          <StepsProgress />

          <CartProvider>
            <CartStatusCheck>
              <Contents>{children}</Contents>
            </CartStatusCheck>
          </CartProvider>
        </ShopStoreProvider>
      </ErrorBoundary>
    </Skeleton>
  </AuthCheck>
);

export default CheckoutLayout;

const Contents: FC = ({ children }) => {
  const { alerts } = useCart();
  const errorAlerts = alerts.filter(({ type }) => type === 'error');

  return (
    <Box width="100%" maxWidth="34em" my={[20, 40]} px={[20, 0]} mx="auto">
      <Box mb={20}>
        <Alerts alerts={errorAlerts} data-testid="checkout-cart-errors" />
      </Box>
      {children}
    </Box>
  );
};
